// @ts-nocheck
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Bundle Builder Component
 *
 * Manages a drawer-based bundle configurator. Reads bundle configuration from
 * a JSON metafield, handles preset switching, product/variant selection,
 * discount calculations, and add-to-cart for all bundle items.
 *
 * @extends DialogComponent
 */
class BundleBuilderComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {Array|null} Parsed bundle presets from metafield JSON */
  #config = null;

  /** @type {number} Index of the currently active preset */
  #activePresetIdx = 0;

  /**
   * Selections map: groupIndex → { productId, variantId, priceCents, discountType, discountValue }
   * @type {Map<number, Object>}
   */
  #selections = new Map();

  /** @type {number|null} Numeric variant ID of the parent product */
  #parentVariantId = null;

  /** @type {number} Price of the parent product variant in cents */
  #parentPriceCents = 0;

  /** @type {string} Title of the parent product */
  #parentTitle = '';

  /** @type {string} Shopify money format string */
  #moneyFormat = '${{amount}}';

  /** @type {string} Currency ISO code */
  #currency = 'USD';

  /** @type {Object|null} Parsed parent variant data (options, variants, selectedVariantId) */
  #parentVariantData = null;

  /** @type {string[]} Currently selected option values, indexed by option position */
  #parentSelectedOptions = [];

  /** @type {boolean} Whether the currently selected parent variant is available for purchase */
  #parentVariantAvailable = true;

  /** @type {AbortController} For cleaning up event listeners */
  #abortController = new AbortController();

  /** @type {Map<string, boolean>} Variant GID → available. Populated from /products/{handle}.js */
  #variantAvailability = new Map();

  /** @type {Map<string, Object>} Product handle → fetched product data cache */
  #productCache = new Map();

  /** @type {Set<number>} Presets whose availability has already been fetched */
  #availabilityFetchedPresets = new Set();

  /** @type {Array|null} Parsed add-on config from metafield JSON */
  #addonConfig = null;

  /**
   * Add-on selections map: addonIndex → { productId, variantId, priceCents, productTitle } | null
   * null means "None" (no add-on selected for that group)
   * @type {Map<number, Object|null>}
   */
  #addonSelections = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#parseConfig();
    this.#parseAddonConfig();
    this.#readAttributes();
    this.#parseParentVariants();

    // Set initial selections from server-rendered defaults.
    // Availability is fetched lazily when the drawer is first opened.
    this.#initSelectionsForPreset(0);
    this.#recalculatePrices();

    // Listen for parent product variant changes
    document.addEventListener(
      'variant:update',
      this.#handleParentVariantUpdate,
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // ─── Public Methods (called via declarative on:* events) ──────────────

  /**
   * Opens the bundle drawer, optionally switching to a specific preset first.
   * Called via: on:click="bundle-builder-component/openDrawer"
   *         or: on:click="bundle-builder-component/openDrawer?presetIdx=N"
   *         or: on:click="#bundle-builder/openDrawer?presetIdx=N"
   */
  openDrawer(dataOrEvent, event) {
    const data =
      dataOrEvent && typeof dataOrEvent === 'object' && 'presetIdx' in dataOrEvent
        ? dataOrEvent
        : null;

    if (data && data.presetIdx !== undefined) {
      const idx = Number(data.presetIdx);
      if (idx !== this.#activePresetIdx && this.#config && idx >= 0 && idx < this.#config.length) {
        this.#activePresetIdx = idx;
        this.#showPresetPanel(idx);
        this.#resetUIForPreset(idx);
        this.#recalculatePrices();
      }
    }

    // Fetch availability lazily on first open per preset
    if (!this.#availabilityFetchedPresets.has(this.#activePresetIdx)) {
      this.#fetchAndApplyAvailability(this.#activePresetIdx);
    }

    this.showDialog();
  }

  /**
   * Switches to a different preset tab.
   * Called via: on:click="bundle-builder-component/switchPreset?presetIdx=N"
   * @param {Object} data - Parsed data from the event attribute
   * @param {number} data.presetIdx - The preset index to switch to
   */
  switchPreset(data) {
    const idx = typeof data === 'object' ? data.presetIdx : data;
    if (idx === this.#activePresetIdx) return;

    this.#activePresetIdx = idx;
    this.#showPresetPanel(idx);
    this.#resetUIForPreset(idx);
    this.#recalculatePrices();

    if (!this.#availabilityFetchedPresets.has(idx)) {
      this.#fetchAndApplyAvailability(idx);
    }
  }

  /**
   * Handles product radio selection within a group.
   * Called via: on:change="bundle-builder-component/selectProduct"
   * @param {Event} event
   */
  selectProduct(event) {
    const radio = event.target;
    if (!radio || radio.type !== 'radio') return;

    const groupIndex = parseInt(radio.dataset.groupIndex, 10);
    const presetIndex = parseInt(radio.dataset.presetIndex, 10);

    // Only process if this radio belongs to the active preset
    if (presetIndex !== this.#activePresetIdx) return;

    const productId = radio.dataset.productId || null;
    const variantId = radio.dataset.variantId || null;
    const priceCents = parseInt(radio.dataset.priceCents, 10) || 0;
    const discountType = radio.dataset.discountType;
    const discountValue = parseFloat(radio.dataset.discountValue) || 0;

    this.#selections.set(groupIndex, {
      productId,
      variantId,
      priceCents,
      discountType,
      discountValue,
    });

    // If the newly selected product has a variant dropdown, reset to its first option
    if (productId) {
      const option = radio.closest('.bundle-product-option');
      const variantSelect = option?.querySelector('[data-variant-select]');
      if (variantSelect) {
        variantSelect.selectedIndex = 0;
        const firstOption = variantSelect.options[0];
        if (firstOption) {
          const selection = this.#selections.get(groupIndex);
          selection.variantId = firstOption.dataset.variantId;
          selection.priceCents = parseInt(firstOption.dataset.priceCents, 10) || 0;

          // Update image to match the first variant
          const img = option?.querySelector('.bundle-product-option__image');
          if (img) {
            const handle = img.dataset.productHandle;
            const variantImage = this.#getVariantImage(handle, firstOption.dataset.variantId);
            img.src = variantImage || img.dataset.defaultImage || img.src;
          }
        }
      }
    }

    this.#updateGroupSummary(groupIndex);
    this.#recalculatePrices();
  }

  /**
   * Handles variant dropdown selection within a product option.
   * Called via: on:change="bundle-builder-component/selectVariant"
   * @param {Event} event
   */
  selectVariant(event) {
    const select = event.target;
    if (!select || select.tagName !== 'SELECT') return;

    const groupIndex = parseInt(select.dataset.groupIndex, 10);
    const presetIndex = parseInt(select.dataset.presetIndex, 10);

    if (presetIndex !== this.#activePresetIdx) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const selection = this.#selections.get(groupIndex);
    if (!selection) return;

    selection.variantId = selectedOption.dataset.variantId;
    selection.priceCents = parseInt(selectedOption.dataset.priceCents, 10) || 0;

    // Update product image to match the selected variant
    const optionEl = select.closest('.bundle-product-option');
    const img = optionEl?.querySelector('.bundle-product-option__image');
    if (img) {
      const handle = img.dataset.productHandle || select.dataset.productHandle;
      const variantImage = this.#getVariantImage(handle, selectedOption.dataset.variantId);
      img.src = variantImage || img.dataset.defaultImage || img.src;
    }

    this.#recalculatePrices();
  }

  /**
   * Handles parent variant option change within the drawer.
   * Called via: on:change="bundle-builder-component/selectParentOption"
   * @param {Event} event
   */
  selectParentOption(event) {
    const select = event.target;
    if (!select || select.tagName !== 'SELECT') return;

    const optionIndex = parseInt(select.dataset.optionIndex, 10);
    if (isNaN(optionIndex) || !this.#parentVariantData) return;

    this.#parentSelectedOptions[optionIndex] = select.value;

    const matchingVariant = this.#findMatchingParentVariant();
    const unavailableEl = this.querySelector('[data-variant-unavailable]');

    if (matchingVariant && matchingVariant.available) {
      this.#parentVariantId = matchingVariant.id;
      this.#parentPriceCents = matchingVariant.price;
      this.#parentVariantAvailable = true;
      this.#recalculatePrices();

      if (unavailableEl) unavailableEl.hidden = true;

      // Update URL so a page reload reflects the selection
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(matchingVariant.id));
      history.replaceState({}, '', url.toString());
    } else if (matchingVariant && !matchingVariant.available) {
      this.#parentVariantId = null;
      this.#parentVariantAvailable = false;
      if (unavailableEl) {
        unavailableEl.textContent = 'This combination is sold out.';
        unavailableEl.hidden = false;
      }
    } else {
      this.#parentVariantId = null;
      this.#parentVariantAvailable = false;
      if (unavailableEl) {
        unavailableEl.textContent = 'This combination is unavailable.';
        unavailableEl.hidden = false;
      }
    }

    this.#updateAddButtonState();
    this.#updateParentOptionAvailability();
  }

  /**
   * Adds the entire bundle to cart.
   * Called via: on:click="bundle-builder-component/addToCart"
   */
  async addToCart() {
    const addButton = this.refs.addButton;
    if (!addButton || addButton.dataset.loading === 'true') return;
    if (!this.#parentVariantAvailable) return;

    addButton.dataset.loading = 'true';
    addButton.disabled = true;

    try {
      const items = this.#buildCartItems();

      if (items.length === 0) return;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          console.error('Bundle add to cart failed:', JSON.parse(errorText));
        } catch {
          console.error('Bundle add to cart failed:', response.status, errorText.slice(0, 200));
        }
        return;
      }

      const result = await response.json();

      // Dispatch CartAddEvent to trigger cart drawer auto-open
      this.dispatchEvent(
        new CartAddEvent(result, this.id || 'bundle-builder', {
          source: 'bundle-builder',
          itemCount: items.length,
        })
      );

      this.closeDialog();
    } catch (error) {
      console.error('Bundle add to cart error:', error);
    } finally {
      addButton.dataset.loading = 'false';
      addButton.disabled = false;
    }
  }

  // ─── Add-On Public Methods ───────────────────────────────────────────

  /**
   * Handles selecting an add-on product within an add-on group.
   * Called via: on:click="bundle-builder-component/selectAddon"
   * @param {Event} event
   */
  selectAddon(event) {
    const btn = event.target.closest('[data-addon-select-btn]');
    if (!btn) return;

    const addonIndex = parseInt(btn.dataset.addonIndex, 10);
    const productId = btn.dataset.productId;
    let variantId = btn.dataset.variantId;
    let priceCents = parseInt(btn.dataset.priceCents, 10) || 0;

    // If there's a variant select for this product, use its current value
    const optionEl = btn.closest('[data-addon-option]');
    const variantSelect = optionEl?.querySelector('[data-addon-variant-select]');
    if (variantSelect) {
      const selectedOpt = variantSelect.options[variantSelect.selectedIndex];
      if (selectedOpt) {
        variantId = selectedOpt.dataset.variantId;
        priceCents = parseInt(selectedOpt.dataset.priceCents, 10) || 0;
      }
    }

    // Find the product title from the addon config
    const addonGroup = this.#addonConfig?.[addonIndex];
    const product = addonGroup?.products?.find(p => String(p.id) === String(productId));
    const productTitle = product?.title || '';

    this.#addonSelections.set(addonIndex, {
      productId,
      variantId,
      priceCents,
      productTitle,
    });

    this.#updateAddonGroupSummary(addonIndex);
    this.#updateAddonOptionStates(addonIndex);
    this.#recalculatePrices();
  }

  /**
   * Handles deselecting an add-on (choosing "None").
   * Called via: on:click="bundle-builder-component/deselectAddon"
   * @param {Event} event
   */
  deselectAddon(event) {
    const btn = event.target.closest('[data-addon-none-btn]');
    if (!btn) return;

    const addonIndex = parseInt(btn.dataset.addonIndex, 10);
    this.#addonSelections.set(addonIndex, null);

    this.#updateAddonGroupSummary(addonIndex);
    this.#updateAddonOptionStates(addonIndex);
    this.#recalculatePrices();
  }

  /**
   * Handles variant dropdown changes within an add-on product.
   * Called via: on:change="bundle-builder-component/selectAddonVariant"
   * @param {Event} event
   */
  selectAddonVariant(event) {
    const select = event.target;
    if (!select || select.tagName !== 'SELECT') return;

    const addonIndex = parseInt(select.dataset.addonIndex, 10);

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    // Always update the image, regardless of selection state
    const optionEl = select.closest('[data-addon-option]');
    const img = optionEl?.querySelector('.bundle-addon-option__image');
    if (img) {
      const handle = optionEl?.dataset.productHandle;
      const variantImage = this.#getVariantImage(handle, selectedOption.dataset.variantId);
      img.src = variantImage || img.dataset.defaultImage || img.src;
    }

    // Only update selection/prices if this product is currently selected
    const selection = this.#addonSelections.get(addonIndex);
    if (!selection || String(selection.productId) !== String(select.dataset.productId)) return;

    selection.variantId = selectedOption.dataset.variantId;
    selection.priceCents = parseInt(selectedOption.dataset.priceCents, 10) || 0;

    this.#recalculatePrices();
  }

  // ─── Private Methods ──────────────────────────────────────────────────

  /**
   * Parses the bundle config JSON from the embedded <script> tag.
   */
  #parseConfig() {
    const scriptTag = this.querySelector('script[data-bundle-config]');
    if (!scriptTag) return;

    try {
      const raw = JSON.parse(scriptTag.textContent);
      // Handle both direct array and stringified JSON
      this.#config = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      console.error('Failed to parse bundle config:', e);
    }
  }

  /**
   * Parses the add-on config JSON from the embedded <script> tag.
   */
  #parseAddonConfig() {
    const scriptTag = this.querySelector('script[data-addon-config]');
    if (!scriptTag) return;

    try {
      this.#addonConfig = JSON.parse(scriptTag.textContent);
      // Initialize all add-on groups to null (no selection)
      if (Array.isArray(this.#addonConfig)) {
        for (let i = 0; i < this.#addonConfig.length; i++) {
          this.#addonSelections.set(i, null);
        }
      }
    } catch (e) {
      console.error('Failed to parse add-on config:', e);
    }
  }

  /**
   * Reads configuration from data attributes.
   */
  #readAttributes() {
    this.#parentVariantId = this.dataset.parentVariantId
      ? parseInt(this.dataset.parentVariantId, 10)
      : null;
    this.#parentPriceCents = this.dataset.parentPrice
      ? parseInt(this.dataset.parentPrice, 10)
      : 0;
    this.#parentTitle = this.dataset.parentTitle || '';
    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.#currency = this.dataset.currency || 'USD';
  }

  /**
   * Parses the parent product variant data from the embedded JSON.
   */
  #parseParentVariants() {
    const scriptTag = this.querySelector('script[data-parent-variants]');
    if (!scriptTag) return;

    try {
      this.#parentVariantData = JSON.parse(scriptTag.textContent);

      if (this.#parentVariantData) {
        const selectedVariant = this.#parentVariantData.variants.find(
          (v) => v.id === this.#parentVariantData.selectedVariantId
        );
        if (selectedVariant) {
          this.#parentSelectedOptions = [...selectedVariant.options];
        }
      }
    } catch (e) {
      console.error('Failed to parse parent variant data:', e);
    }
  }

  /**
   * Finds the variant matching the currently selected parent options.
   * @returns {Object|null}
   */
  #findMatchingParentVariant() {
    if (!this.#parentVariantData) return null;

    return (
      this.#parentVariantData.variants.find((variant) =>
        variant.options.every((opt, idx) => opt === this.#parentSelectedOptions[idx])
      ) || null
    );
  }

  /**
   * Updates the enabled/disabled state of parent option select values
   * based on which combinations actually exist as variants.
   */
  #updateParentOptionAvailability() {
    if (!this.#parentVariantData) return;

    const selects = this.querySelectorAll('[data-parent-option-select]');

    selects.forEach((select) => {
      const optionIndex = parseInt(select.dataset.optionIndex, 10);

      for (const option of select.options) {
        const hypothetical = [...this.#parentSelectedOptions];
        hypothetical[optionIndex] = option.value;

        const hasMatch = this.#parentVariantData.variants.some((variant) =>
          variant.options.every((opt, idx) =>
            idx === optionIndex ? opt === option.value : opt === this.#parentSelectedOptions[idx]
          )
        );

        const hasAvailableMatch = this.#parentVariantData.variants.some(
          (variant) =>
            variant.available &&
            variant.options.every((opt, idx) =>
              idx === optionIndex ? opt === option.value : opt === this.#parentSelectedOptions[idx]
            )
        );

        // Clean any previous status text before re-evaluating
        const cleanText = option.textContent.replace(/ \(unavailable\)$/, '').replace(/ \(sold out\)$/, '').trim();

        if (!hasMatch) {
          option.disabled = true;
          option.textContent = cleanText + ' (unavailable)';
        } else if (!hasAvailableMatch) {
          option.disabled = false;
          option.textContent = cleanText + ' (sold out)';
        } else {
          option.disabled = false;
          option.textContent = cleanText;
        }
      }
    });
  }

  /**
   * Enables or disables the add-to-cart button based on parent variant availability.
   */
  #updateAddButtonState() {
    const addButton = this.refs.addButton;
    if (!addButton) return;

    addButton.disabled = !this.#parentVariantAvailable;
  }

  /**
   * Updates the in-drawer parent option selects to reflect a variant
   * that was selected on the product page.
   * @param {number} variantId
   */
  #syncDrawerSelectsFromVariantId(variantId) {
    if (!this.#parentVariantData || !variantId) return;

    const variant = this.#parentVariantData.variants.find((v) => v.id === variantId);
    if (!variant) return;

    this.#parentSelectedOptions = [...variant.options];

    const selects = this.querySelectorAll('[data-parent-option-select]');
    selects.forEach((select) => {
      const optionIndex = parseInt(select.dataset.optionIndex, 10);
      if (!isNaN(optionIndex) && variant.options[optionIndex]) {
        select.value = variant.options[optionIndex];
      }
    });

    this.#updateParentOptionAvailability();
  }

  /**
   * Initializes the selections map from the DOM for a given preset.
   * Reads checked radio inputs to determine selected products/variants.
   * @param {number} presetIdx
   */
  #initSelectionsForPreset(presetIdx) {
    this.#selections.clear();

    const panel = this.querySelector(`[data-preset-panel="${presetIdx}"]`);
    if (!panel) return;

    const checkedRadios = panel.querySelectorAll('input[type="radio"]:checked');

    for (const input of checkedRadios) {
      const groupIndex = parseInt(input.dataset.groupIndex, 10);
      if (isNaN(groupIndex)) continue;

      this.#selections.set(groupIndex, {
        productId: input.dataset.productId,
        variantId: input.dataset.variantId,
        priceCents: parseInt(input.dataset.priceCents, 10) || 0,
        discountType: input.dataset.discountType || '',
        discountValue: parseFloat(input.dataset.discountValue) || 0,
      });
    }
  }

  /**
   * Resets the UI and selections for a preset panel back to server-rendered defaults.
   * @param {number} presetIdx
   */
  #resetUIForPreset(presetIdx) {
    const panel = this.querySelector(`[data-preset-panel="${presetIdx}"]`);
    if (!panel) return;

    // Find all groups in this panel
    const groups = panel.querySelectorAll('[data-bundle-group]');
    for (const groupEl of groups) {
      // Reset radios: find the one with the HTML checked attribute (server default)
      const radios = groupEl.querySelectorAll('input[type="radio"]');
      let defaultFound = false;
      for (const radio of radios) {
        if (radio.hasAttribute('checked')) {
          radio.checked = true;
          defaultFound = true;
        }
      }
      // If no default attribute found, check the first radio
      if (!defaultFound && radios.length > 0) {
        radios[0].checked = true;
      }

      // Reset variant selects to their default (first option or selected attribute)
      const selects = groupEl.querySelectorAll('[data-variant-select]');
      for (const select of selects) {
        let defaultIdx = 0;
        for (let i = 0; i < select.options.length; i++) {
          if (select.options[i].hasAttribute('selected')) {
            defaultIdx = i;
            break;
          }
        }
        select.selectedIndex = defaultIdx;
      }

      // Reset product images to their defaults
      const images = groupEl.querySelectorAll('[data-default-image]');
      for (const img of images) {
        img.src = img.dataset.defaultImage;
      }
    }

    // Re-read selections from DOM after reset
    this.#initSelectionsForPreset(presetIdx);
  }

  /**
   * Shows the specified preset panel and hides others. Updates tab states.
   * @param {number} idx
   */
  #showPresetPanel(idx) {
    // Toggle panels
    const panels = this.querySelectorAll('[data-preset-panel]');
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.presetPanel !== String(idx);
    });

    // Toggle tab active states
    const tabs = this.querySelectorAll('[data-preset-tab]');
    tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });
  }

  /**
   * Recalculates all prices and updates the UI.
   * Builds an itemized list of selected bundle items, includes the parent
   * product price, and computes the grand total.
   */
  #recalculatePrices() {
    if (!this.#config) return;

    const preset = this.#config[this.#activePresetIdx];
    if (!preset) return;

    let totalAddonCents = 0;

    for (const [groupIdx, selection] of this.#selections) {
      const group = preset.groups[groupIdx];
      if (!group) continue;

      const child = this.#findChild(group, selection.productId);
      const discount = child
        ? this.#getDiscountForChild(group, child)
        : { type: selection.discountType, value: selection.discountValue };

      const discountedCents = this.#applyDiscount(selection.priceCents, discount);
      totalAddonCents += discountedCents;
    }

    // Sum optional add-on selections
    for (const [, addonSelection] of this.#addonSelections) {
      if (addonSelection) {
        totalAddonCents += addonSelection.priceCents;
      }
    }

    // Update bundle items subtotal
    if (this.refs.addonTotal) {
      this.refs.addonTotal.textContent = this.#formatPrice(totalAddonCents);
    }

    // Update parent price display
    if (this.refs.parentPrice) {
      this.refs.parentPrice.textContent = this.#formatPrice(this.#parentPriceCents);
    }

    // Grand total = parent price + all add-on items
    const grandTotalCents = this.#parentPriceCents + totalAddonCents;

    // Update total price display
    if (this.refs.totalPrice) {
      this.refs.totalPrice.textContent = this.#formatPrice(grandTotalCents);
    }

    // Update price delta in header (add-on cost only)
    if (this.refs.priceDelta) {
      if (totalAddonCents > 0) {
        this.refs.priceDelta.textContent = '+' + this.#formatPrice(totalAddonCents);
      } else {
        this.refs.priceDelta.textContent = '';
      }
    }
  }

  /**
   * Returns the applicable discount for a child product.
   * Child-level overrides take precedence over group-level.
   * @param {Object} group
   * @param {Object} child
   * @returns {{ type: string, value: number }}
   */
  #getDiscountForChild(group, child) {
    if (child && child.hasDiscountOverride) {
      return {
        type: child.discountType,
        value: child.discountValue,
      };
    }
    return {
      type: group.discountType,
      value: group.discountValue,
    };
  }

  /**
   * Applies a discount to a price in cents.
   * @param {number} priceCents
   * @param {{ type: string, value: number }} discount
   * @returns {number} Discounted price in cents, floored at 0
   */
  #applyDiscount(priceCents, discount) {
    if (!discount || !discount.type) return priceCents;

    let result;
    if (discount.type === 'percentage') {
      result = priceCents - Math.round(priceCents * discount.value / 100);
    } else if (discount.type === 'fixed') {
      result = priceCents - Math.round(discount.value * 100);
    } else {
      result = priceCents;
    }

    return Math.max(0, result);
  }

  /**
   * Formats a price in cents using the shop's money format.
   * @param {number} cents
   * @returns {string}
   */
  #formatPrice(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }

  /**
   * Finds a child product within a group by product ID.
   * @param {Object} group
   * @param {string} productId
   * @returns {Object|undefined}
   */
  #findChild(group, productId) {
    return group.children.find((c) => c.productId === productId);
  }

  /**
   * Finds the image URL for a specific variant from the product cache.
   * Falls back to null if the variant has no dedicated image or the product
   * hasn't been fetched yet.
   * @param {string} handle - Product handle
   * @param {string} variantId - Variant ID (numeric or GID)
   * @returns {string|null}
   */
  #getVariantImage(handle, variantId) {
    if (!handle || !variantId) return null;
    const product = this.#productCache.get(handle);
    if (!product) return null;

    const numericId = String(variantId).match(/(\d+)$/)?.[1];
    if (!numericId) return null;

    const variant = product.variants.find((v) => String(v.id) === numericId);
    return variant?.featured_image?.src || null;
  }

  /**
   * Updates the collapsed group summary (selected product name and badge).
   * @param {number} groupIdx
   */
  #updateGroupSummary(groupIdx) {
    const preset = this.#config?.[this.#activePresetIdx];
    if (!preset) return;

    const group = preset.groups[groupIdx];
    const selection = this.#selections.get(groupIdx);
    if (!group || !selection) return;

    // Find the group element in the active panel
    const panel = this.querySelector(`[data-preset-panel="${this.#activePresetIdx}"]`);
    if (!panel) return;

    const groupEl = panel.querySelector(`[data-bundle-group][data-group-index="${groupIdx}"]`);
    if (!groupEl) return;

    const nameEl = groupEl.querySelector('[data-group-selected-name]');
    const badgeEl = groupEl.querySelector('[data-group-discount-badge]');

    // Handle "None" selection
    if (!selection.productId) {
      if (nameEl) nameEl.textContent = 'None';
      if (badgeEl) badgeEl.textContent = '';
      return;
    }

    const child = this.#findChild(group, selection.productId);
    if (!child) return;

    // Update selected name
    if (nameEl) {
      nameEl.textContent = child.productTitle;
    }

    // Update discount badge
    const discount = this.#getDiscountForChild(group, child);
    const discountedCents = this.#applyDiscount(selection.priceCents, discount);
    if (badgeEl) {
      if (discountedCents === 0) {
        badgeEl.textContent = 'Included';
      } else if (child.hasDiscountOverride && child.discountLabel) {
        badgeEl.textContent = child.discountLabel;
      } else if (group.discountLabel) {
        badgeEl.textContent = group.discountLabel;
      }
    }
  }

  /**
   * Updates the collapsed summary text for an add-on group.
   * @param {number} addonIndex
   */
  #updateAddonGroupSummary(addonIndex) {
    const groupEl = this.querySelector(`[data-bundle-addon-group][data-addon-index="${addonIndex}"]`);
    if (!groupEl) return;

    const nameEl = groupEl.querySelector('[data-addon-selected-name]');
    if (!nameEl) return;

    const selection = this.#addonSelections.get(addonIndex);
    nameEl.textContent = selection ? selection.productTitle : 'None';
  }

  /**
   * Updates the visual state of add-on option cards (active/inactive).
   * @param {number} addonIndex
   */
  #updateAddonOptionStates(addonIndex) {
    const groupEl = this.querySelector(`[data-bundle-addon-group][data-addon-index="${addonIndex}"]`);
    if (!groupEl) return;

    const selection = this.#addonSelections.get(addonIndex);
    const options = groupEl.querySelectorAll('[data-addon-option]');

    for (const optionEl of options) {
      const isSelected = selection && String(optionEl.dataset.productId) === String(selection.productId);
      optionEl.classList.toggle('bundle-addon-option--selected', isSelected);
      const btn = optionEl.querySelector('[data-addon-select-btn]');
      if (btn) {
        btn.textContent = isSelected ? 'Added' : 'Add';
      }
    }

    // Update "None" button state
    const noneBtn = groupEl.querySelector('[data-addon-none-btn]');
    if (noneBtn) {
      noneBtn.classList.toggle('bundle-addon-option__none-btn--active', !selection);
    }
  }

  /**
   * Fetches product availability from Shopify AJAX API and applies it to the UI.
   * Sold-out products are disabled; sold-out defaults fall back to the next available child.
   * @param {number} presetIdx
   */
  async #fetchAndApplyAvailability(presetIdx) {
    const preset = this.#config?.[presetIdx];
    if (!preset) return;

    // Collect unique product handles that haven't been cached yet
    const handles = [];
    for (const group of preset.groups) {
      for (const child of group.children) {
        if (child.productHandle && !this.#productCache.has(child.productHandle)) {
          handles.push(child.productHandle);
        }
      }
    }

    // Also collect add-on product handles for variant image lookups
    if (this.#addonConfig) {
      for (const addonGroup of this.#addonConfig) {
        for (const product of addonGroup.products || []) {
          if (product.handle && !this.#productCache.has(product.handle)) {
            handles.push(product.handle);
          }
        }
      }
    }

    // De-duplicate
    const uniqueHandles = [...new Set(handles)];

    // Fetch with concurrency limit to avoid 429 rate limiting
    const CONCURRENCY = 3;
    for (let i = 0; i < uniqueHandles.length; i += CONCURRENCY) {
      const batch = uniqueHandles.slice(i, i + CONCURRENCY);
      await Promise.allSettled(batch.map((handle) => this.#fetchProduct(handle)));
    }

    this.#availabilityFetchedPresets.add(presetIdx);
    this.#applyAvailability(presetIdx);
  }

  /**
   * Fetches a single product's data with retry on 429.
   * @param {string} handle
   * @param {number} attempt
   */
  async #fetchProduct(handle, attempt = 0) {
    try {
      const res = await fetch(`/products/${handle}.js`);

      if (res.status === 429 && attempt < 3) {
        const delay = 1000 * (attempt + 1); // 1s, 2s, 3s
        await new Promise((r) => setTimeout(r, delay));
        return this.#fetchProduct(handle, attempt + 1);
      }

      if (res.ok) {
        const product = await res.json();
        this.#productCache.set(handle, product);
        for (const variant of product.variants) {
          this.#variantAvailability.set(String(variant.id), variant.available);
        }
      }
    } catch {
      // If fetch fails, leave availability unknown (treated as available)
    }
  }

  /**
   * Checks whether a variant GID or numeric ID is available for purchase.
   * Returns true if availability is unknown (fetch failed or not yet loaded).
   * @param {string} variantId - GID or numeric variant ID
   * @returns {boolean}
   */
  #isVariantAvailable(variantId) {
    if (!variantId) return false;
    const numericId = String(variantId).match(/(\d+)$/)?.[1];
    if (!numericId) return false;
    const available = this.#variantAvailability.get(numericId);
    return available !== false; // unknown = available
  }

  /**
   * Applies availability state to the DOM for a given preset.
   * Disables sold-out radios/options, shifts defaults to available products.
   * @param {number} presetIdx
   */
  #applyAvailability(presetIdx) {
    const panel = this.querySelector(`[data-preset-panel="${presetIdx}"]`);
    if (!panel) return;

    const groups = panel.querySelectorAll('[data-bundle-group]');

    for (const groupEl of groups) {
      // ── Expandable groups (radio inputs) ──
      const radios = groupEl.querySelectorAll('input[type="radio"]');
      if (radios.length > 0) {
        let checkedRadio = groupEl.querySelector('input[type="radio"]:checked');
        let checkedIsSoldOut = checkedRadio && !this.#isVariantAvailable(checkedRadio.dataset.variantId);

        // Mark each product option as sold out or available
        for (const radio of radios) {
          // Skip the "None" option — it has no variant and is always available
          if (!radio.dataset.productId) continue;

          const option = radio.closest('.bundle-product-option');
          const available = this.#isVariantAvailable(radio.dataset.variantId);
          radio.disabled = !available;
          if (option) {
            option.classList.toggle('bundle-product-option--sold-out', !available);
          }
        }

        // If checked default is sold out, select the first available radio
        if (checkedIsSoldOut) {
          for (const radio of radios) {
            if (!radio.disabled) {
              radio.checked = true;
              // Update the variant select to its first available option too
              const option = radio.closest('.bundle-product-option');
              const variantSelect = option?.querySelector('[data-variant-select]');
              if (variantSelect) {
                this.#selectFirstAvailableOption(variantSelect);
                const firstOpt = variantSelect.options[variantSelect.selectedIndex];
                if (firstOpt) {
                  radio.dataset.variantId = firstOpt.dataset.variantId;
                  radio.dataset.priceCents = firstOpt.dataset.priceCents;
                }
              }
              break;
            }
          }
        }

        // Handle variant dropdowns: disable sold-out options
        for (const radio of radios) {
          const option = radio.closest('.bundle-product-option');
          const variantSelect = option?.querySelector('[data-variant-select]');
          if (!variantSelect) continue;

          for (const opt of variantSelect.options) {
            const optAvailable = this.#isVariantAvailable(opt.dataset.variantId);
            opt.disabled = !optAvailable;
            const cleanText = opt.textContent.replace(/ \(sold out\)$/, '').trim();
            opt.textContent = optAvailable ? cleanText : cleanText + ' (sold out)';
          }

          // If the currently selected option is sold out, pick the first available
          const selectedOpt = variantSelect.options[variantSelect.selectedIndex];
          if (selectedOpt?.disabled) {
            this.#selectFirstAvailableOption(variantSelect);
          }
        }
      }

    }

    // Re-read selections from the now-adjusted DOM
    this.#initSelectionsForPreset(presetIdx);
    this.#recalculatePrices();

    // Update group summaries to reflect any default changes
    const preset = this.#config?.[presetIdx];
    if (preset) {
      for (let i = 0; i < preset.groups.length; i++) {
        this.#updateGroupSummary(i);
      }
    }
  }

  /**
   * Selects the first non-disabled option in a <select> element.
   * @param {HTMLSelectElement} select
   */
  #selectFirstAvailableOption(select) {
    for (let i = 0; i < select.options.length; i++) {
      if (!select.options[i].disabled) {
        select.selectedIndex = i;
        return;
      }
    }
  }

  /**
   * Builds the cart items array for the add-to-cart POST.
   * Includes the parent product and all selected child products.
   * @returns {Array<Object>}
   */
  #buildCartItems() {
    if (!this.#config || !this.#parentVariantId) return [];

    const preset = this.#config[this.#activePresetIdx];
    if (!preset) return [];

    const bundleId = this.#generateBundleId();
    const items = [];

    // Count active selections (exclude "None" groups) and add-ons for component count
    const activeGroupCount = [...this.#selections.values()].filter(s => s.productId).length;
    const activeAddonCount = [...this.#addonSelections.values()].filter(Boolean).length;

    // Parent product (the bike frame)
    items.push({
      id: this.#parentVariantId,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_role: 'parent',
        _bundle_preset: preset.title,
        _bundle_component_count: String(activeGroupCount + activeAddonCount),
      },
    });

    // Child products (bundle components)
    for (const [groupIdx, selection] of this.#selections) {
      if (!selection.productId) continue; // Skip "None" selections

      const group = preset.groups[groupIdx];
      if (!group) continue;

      const numericId = this.#numericVariantId(selection.variantId);
      if (!numericId) continue;

      items.push({
        id: numericId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'child',
          _bundle_preset: preset.title,
          _bundle_group: group.name,
        },
      });
    }

    // Optional add-on products
    for (const [addonIdx, addonSelection] of this.#addonSelections) {
      if (!addonSelection) continue;

      const numericId = this.#numericVariantId(addonSelection.variantId);
      if (!numericId) continue;

      const addonGroup = this.#addonConfig?.[addonIdx];
      const groupName = addonGroup?.title || `Add-On ${addonIdx + 1}`;

      items.push({
        id: numericId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'child',
          _bundle_preset: preset.title,
          _bundle_group: groupName,
          _bundle_addon: 'true',
        },
      });
    }

    return items;
  }

  /**
   * Extracts the numeric variant ID from a Shopify GID string.
   * e.g., "gid://shopify/ProductVariant/44132543037638" → 44132543037638
   * @param {string} gid
   * @returns {number|null}
   */
  #numericVariantId(gid) {
    if (!gid) return null;
    // Handle already-numeric IDs
    if (typeof gid === 'number') return gid;
    const match = String(gid).match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Generates a unique bundle ID for linking cart items.
   * @returns {string}
   */
  #generateBundleId() {
    return `bundle-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Handles parent product variant changes from the variant picker.
   * @param {Event} event
   */
  #handleParentVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    const variantId = variant.id ? parseInt(variant.id, 10) : null;

    // Check availability against our local variant data
    const localVariant = variantId && this.#parentVariantData
      ? this.#parentVariantData.variants.find((v) => v.id === variantId)
      : null;
    const isAvailable = localVariant ? localVariant.available : variant.available !== false;

    if (variantId) {
      this.#parentVariantId = isAvailable ? variantId : null;
    }
    if (variant.price != null) {
      this.#parentPriceCents = parseInt(variant.price, 10);
      this.#recalculatePrices();
    }

    this.#parentVariantAvailable = isAvailable;
    this.#updateAddButtonState();

    // Sync the drawer selects to match the variant selected on the product page
    if (variantId) {
      this.#syncDrawerSelectsFromVariantId(variantId);
    }
  };
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
